import { Button, Input, ProgressLoader, Search, Text, VSpacer } from '@/components/DesignSystem';
import { Table } from '@/components/DesignSystem/Table/Table';
import {
  CustomerAutocomplete,
  CustomerOption,
  toCustomerOption,
} from '@/components/shared/CustomerAutocomplete';
import { useDefaultRetailerId } from '@/components/shared/DefaultRetailerSelector';
import { QueryKeys } from '@/constants/QueryKeys';
import { LocalStorageUtility } from '@/utilities/LocalStorageUtility';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { UserInspectorApi } from '@/utilities/api/UserInspectorApi';
import { formatCell } from './formatCell';
import { UserInspectorEndpoint } from '@api/endpoints';
import Check from '@mui/icons-material/Check';
import ContentCopy from '@mui/icons-material/ContentCopy';
import DragIndicator from '@mui/icons-material/DragIndicator';
import ExpandMore from '@mui/icons-material/ExpandMore';
import GridOn from '@mui/icons-material/GridOn';
import SearchIcon from '@mui/icons-material/Search';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  TableSortLabel,
  TextField,
  Tooltip,
} from '@mui/material';
import { UserType } from '@shared/enums';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';

type SortDirection = 'asc' | 'desc';

interface SubmittedQuery {
  userAccountId: string;
  includeGroup: boolean;
  pageSize: number;
}

// The fixed AppHeader is 64px; STICKY_TOP also clears the sticky user header so
// jump targets and the pinned nav land below both bars.
const STICKY_TOP = 128;
const PAGE_SIZES = [50, 100, 200, 500, 1000];
const DEFAULT_PAGE_SIZE = 200;
const sectionElementId = (table: string) => `ui-table-${table}`;

const DEFAULT_TABLE_ORDER = [
  'UserAccount',
  'FarmerRewards',
  'FarmerTierHistory',
  'Subwallet',
  'SubwalletBalance',
  'PointsHistory',
  'OrderTransaction',
  'OrderTransactionProduct',
  'Notification',
  'ScheduledNotification',
  'Promotion',
  'AccountGroup',
  'Product',
  'Manufacturer',
  'Category',
  'Retailer',
  'RetailerLocation',
  'County',
  'RewardsAccount',
  'RewardsProgram',
  'FarmerSalesperson',
  'ImportHistory',
  'UserLoyaltyPointsHistory',
  'UserLoyaltyPointsWallet',
];

const LAYOUT_STORAGE_KEY = 'userInspector.layout';

interface StoredLayout { order: string[]; hidden: string[] }

const loadLayout = (): StoredLayout => {
  const empty: StoredLayout = { hidden: [], order: [] };
  try {
    const raw = LocalStorageUtility.getItem(LAYOUT_STORAGE_KEY);
    if (!raw) return empty;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return empty;
    const record = parsed as Record<string, unknown>;
    const strings = (value: unknown): string[] =>
      (Array.isArray(value) ? value.filter((x): x is string => typeof x === 'string') : []);
    return { hidden: strings(record.hidden), order: strings(record.order) };
  } catch {
    return empty;
  }
};

export const UserInspector = () => {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const initialId = searchParams.get('id')?.trim() ?? '';
  const initialGroup = searchParams.get('group') === '1';
  const capParam = Number(searchParams.get('cap'));
  const initialPageSize = PAGE_SIZES.includes(capParam) ? capParam : DEFAULT_PAGE_SIZE;

  const [idInput, setIdInput] = useState(initialId);
  const { defaultRetailerId } = useDefaultRetailerId();
  const [retailerId, setRetailerId] = useState(defaultRetailerId ?? '');
  const [customer, setCustomer] = useState<CustomerOption | null>(null);
  const [includeGroup, setIncludeGroup] = useState(initialGroup);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [submitted, setSubmitted] = useState<SubmittedQuery | undefined>(
    initialId
      ? { includeGroup: initialGroup, pageSize: initialPageSize, userAccountId: initialId }
      : undefined,
  );
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(loadLayout().hidden));
  const [order, setOrder] = useState<string[]>(() => loadLayout().order);
  const [loadedCounts, setLoadedCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    LocalStorageUtility.set(LAYOUT_STORAGE_KEY, { hidden: [...hidden], order });
  }, [order, hidden]);

  const resetLayout = () => {
    setOrder([]);
    setHidden(new Set());
  };

  const reportCount = useCallback((table: string, count: number) => {
    setLoadedCounts((prev) => (prev[table] === count ? prev : { ...prev, [table]: count }));
  }, []);

  const { data, isFetching, error } = useQuery<
    UserInspectorEndpoint.GetUserData.Response, Error
  >(
    [QueryKeys.GET_USER_INSPECTION, submitted?.userAccountId, submitted?.includeGroup,
      submitted?.pageSize],
    () => UserInspectorApi.getUserData(submitted?.userAccountId ?? '', {
      cap: submitted?.pageSize,
      includeGroup: submitted?.includeGroup || undefined,
    }),
    { enabled: !!submitted?.userAccountId, retry: false },
  );

  const { data: retailers } = useQuery(
    [QueryKeys.GET_RETAILERS],
    () => RetailerApi.list({ limit: 200 }),
  );
  const retailerOptions = retailers?.data ?? [];
  // A pasted user's retailer may be missing from the list, which a select can't show.
  const selectedRetailerId = retailerOptions.some(({ id }) => id === retailerId) ? retailerId : '';

  const onRetailerChange = (id: string) => {
    setRetailerId(id);
    setCustomer(null);
  };


  const urlParams = (id: string, group: boolean, size: number) => {
    const params: Record<string, string> = { id };
    if (group) params.group = '1';
    if (size !== DEFAULT_PAGE_SIZE) params.cap = String(size);
    return params;
  };

  const inspect = (userAccountId: string) => {
    const trimmed = userAccountId.trim();
    if (trimmed) {
      setLoadedCounts({});
      setSubmitted({ includeGroup, pageSize, userAccountId: trimmed });
      setSearchParams(urlParams(trimmed, includeGroup, pageSize));
    }
  };

  const onInspect = () => inspect(idInput);

  const onCustomerChange = (selected: CustomerOption | null) => {
    setCustomer(selected);
    if (selected) {
      setIdInput(selected.id);
      inspect(selected.id);
    }
  };

  const onPageSizeChange = (size: number) => {
    setPageSize(size);
    if (submitted) {
      setLoadedCounts({});
      setSubmitted({ ...submitted, pageSize: size });
      setSearchParams(urlParams(submitted.userAccountId, submitted.includeGroup, size));
    }
  };

  const onIncludeGroupChange = (checked: boolean) => {
    setIncludeGroup(checked);
    if (submitted) {
      setLoadedCounts({});
      setSubmitted({ ...submitted, includeGroup: checked });
      setSearchParams(urlParams(submitted.userAccountId, checked, submitted.pageSize));
    }
  };

  const toggleHidden = (table: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(table)) next.delete(table);
      else next.add(table);
      return next;
    });
  };

  const jumpTo = (table: string) => {
    document.getElementById(sectionElementId(table))
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // The root user's own row (the UserAccount table may also hold terminal
  // reference rows for other users, so match on the inspected id).
  const rootUser = data?.tables
    .find((tbl) => tbl.table === 'UserAccount')?.rows
    .find((row) => row.id === data.userAccountId);

  // Reflect a pasted id in the pickers once its user loads.
  useEffect(() => {
    if (!rootUser) {
      return;
    }
    const textOrNull = (value: unknown) => formatCell(value) || null;
    setRetailerId(formatCell(rootUser.retailerId));
    setCustomer(toCustomerOption({
      businessName: textOrNull(rootUser.businessName),
      email: textOrNull(rootUser.email),
      externalId: textOrNull(rootUser.externalId),
      firstName: textOrNull(rootUser.firstName),
      id: formatCell(rootUser.id),
      lastName: textOrNull(rootUser.lastName),
      telephone: textOrNull(rootUser.telephone),
      userType: Object.values(UserType).find((type) => type === rootUser.userType)
        ?? UserType.Farmer,
    }));
  }, [rootUser]);

  // Retailer name lives on the Retailer table, not the user row — look it up by
  // the user's retailerId among the fetched Retailer rows.
  const retailerName = formatCell(
    data?.tables
      .find((tbl) => tbl.table === 'Retailer')?.rows
      .find((row) => row.id === rootUser?.retailerId)?.name,
  );

  // Order tables by: the user's manual drag order first, then the curated
  // default order, then anything left over (new/unknown tables) in server order.
  const orderedTables = useMemo(() => {
    const tables = data?.tables ?? [];
    const byName = new Map(tables.map((tbl) => [tbl.table, tbl]));
    const seen = new Set<string>();
    const result: UserInspectorEndpoint.GetUserData.InspectedTable[] = [];
    const take = (name: string) => {
      const tbl = byName.get(name);
      if (tbl && !seen.has(name)) {
        seen.add(name);
        result.push(tbl);
      }
    };
    order.forEach(take);
    DEFAULT_TABLE_ORDER.forEach(take);
    tables.forEach((tbl) => take(tbl.table));
    return result;
  }, [data, order]);

  return (
    <Container maxWidth={false}>
      <VSpacer size="7" />
      <Text category="headline-medium">{t(($) => $.userInspector.title)}</Text>
      <VSpacer size="2" />
      <Text category="body-medium">{t(($) => $.userInspector.description)}</Text>
      <VSpacer size="5" />

      <Stack alignItems="flex-end" direction="row" flexWrap="wrap" gap={2} useFlexGap>
        <TextField
          data-testid="user-inspector-retailer"
          label={t(($) => $.userInspector.retailer)}
          onChange={(event) => onRetailerChange(event.target.value)}
          select
          sx={{ minWidth: 260 }}
          value={selectedRetailerId}
        >
          {retailerOptions.map((retailer) => (
            <MenuItem key={retailer.id} value={retailer.id}>{retailer.name}</MenuItem>
          ))}
        </TextField>
        <CustomerAutocomplete
          label={t(($) => $.userInspector.customer)}
          onChange={onCustomerChange}
          retailerId={selectedRetailerId}
          testID="user-inspector-customer"
          value={customer}
        />
        <Input
          label={t(($) => $.userInspector.idLabel)}
          onChangeText={setIdInput}
          onEnterPressed={onInspect}
          placeholder={t(($) => $.userInspector.idPlaceholder)}
          testID="user-inspector-id"
          trailingIcon={() => (
            <Tooltip title={t(($) => $.userInspector.submit)}>
              <span>
                <IconButton
                  aria-label={t(($) => $.userInspector.submit)}
                  data-testid="user-inspector-inspect"
                  disabled={!idInput.trim()}
                  onClick={onInspect}
                  sx={{ mr: '10px' }}
                >
                  {isFetching
                    ? <CircularProgress size={26} />
                    : <SearchIcon sx={{ fontSize: 28 }} />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          value={idInput}
          width={320}
        />
        <FormControlLabel
          control={(
            <Checkbox
              checked={includeGroup}
              onChange={(e) => onIncludeGroupChange(e.target.checked)}
            />
          )}
          label={t(($) => $.userInspector.includeGroup)}
        />
        <TextField
          label={t(($) => $.userInspector.pageSize)}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          select
          size="small"
          sx={{ width: 120 }}
          value={pageSize}
        >
          {PAGE_SIZES.map((size) => (
            <MenuItem key={size} value={size}>{size}</MenuItem>
          ))}
        </TextField>
      </Stack>
      <VSpacer size="2" />
      <Text category="body-small">{t(($) => $.userInspector.redactionNote)}</Text>
      <VSpacer size="6" />
      <Divider />
      <VSpacer size="6" />

      {!submitted && (
        <Text category="body-medium">{t(($) => $.userInspector.emptyPrompt)}</Text>
      )}
      {isFetching && (
        <Stack alignItems="center"><ProgressLoader type="circular" /></Stack>
      )}
      {!isFetching && error && (
        <Text category="body-medium">{t(($) => $.userInspector.notFound)}</Text>
      )}
      {!isFetching && !error && data && (
        <>
          {rootUser && (
            <UserHeader
              retailerName={retailerName}
              row={rootUser}
              userAccountId={data.userAccountId}
            />
          )}
          <VSpacer size="4" />
          <Text category="title-medium">
            {t(($) => $.userInspector.summary, {
              tableCount: data.tableCount,
              totalRows: data.totalRows,
            })}
          </Text>
          {data.traversalCapped.length > 0 && (
            <>
              <VSpacer size="3" />
              <Alert severity="warning">
                {t(($) => $.userInspector.traversalCappedWarning, {
                  tables: data.traversalCapped.join(', '),
                })}
              </Alert>
            </>
          )}
          <VSpacer size="4" />
          <Box sx={{ display: 'flex', gap: 3 }}>
            <TableNav
              counts={loadedCounts}
              hidden={hidden}
              onJump={jumpTo}
              onReorder={setOrder}
              onReset={resetLayout}
              onToggleHidden={toggleHidden}
              tables={orderedTables}
            />
            <Stack flex={1} minWidth={0} spacing={6}>
              {orderedTables
                .filter((table) => !hidden.has(table.table))
                .map((table) => (
                  <InspectedTable
                    key={table.table}
                    onCountChange={reportCount}
                    pageSize={pageSize}
                    table={table}
                  />
                ))}
            </Stack>
          </Box>
        </>
      )}
    </Container>
  );
};

const TableNav = ({
  counts,
  hidden,
  onJump,
  onReorder,
  onReset,
  onToggleHidden,
  tables,
}: {
  counts: Record<string, number>,
  hidden: Set<string>,
  onJump: (table: string) => void,
  onReorder: (names: string[]) => void,
  onReset: () => void,
  onToggleHidden: (table: string) => void,
  tables: UserInspectorEndpoint.GetUserData.InspectedTable[],
}) => {
  const { t } = useTranslation();
  const dragName = useRef<string | null>(null);
  const [overName, setOverName] = useState<string | null>(null);

  const onDrop = (target: string) => {
    const source = dragName.current;
    dragName.current = null;
    setOverName(null);
    if (!source || source === target) return;

    const names = tables.map((tbl) => tbl.table);
    const from = names.indexOf(source);
    const to = names.indexOf(target);
    if (from < 0 || to < 0) return;

    const next = [...names];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  return (
    <Box
      component="nav"
      sx={{
        alignSelf: 'flex-start',
        flexShrink: 0,
        maxHeight: `calc(100vh - ${STICKY_TOP + 24}px)`,
        overflowY: 'auto',
        position: 'sticky',
        // Size to the widest entry so no table name/count wraps.
        pr: 1,
        top: `${STICKY_TOP}px`,
        width: 'max-content',
      }}
    >
      <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={2}>
        <Text category="label-large">{t(($) => $.userInspector.tablesNav)}</Text>
        <Link
          component="button"
          onClick={onReset}
          underline="hover"
          variant="body2"
        >
          {t(($) => $.userInspector.resetLayout)}
        </Link>
      </Stack>
      <VSpacer size="2" />
      <Stack spacing={0.5}>
        {tables.map((table) => {
          const isHidden = hidden.has(table.table);
          const label = `${table.table} (${counts[table.table] ?? table.rowCount})`;
          return (
            <Stack
              alignItems="center"
              direction="row"
              draggable
              key={table.table}
              onDragEnd={() => { dragName.current = null; setOverName(null); }}
              onDragOver={(e) => {
                e.preventDefault();
                if (overName !== table.table) setOverName(table.table);
              }}
              onDragStart={() => { dragName.current = table.table; }}
              onDrop={() => onDrop(table.table)}
              spacing={0.5}
              sx={{
                borderTop: '2px solid',
                borderTopColor: overName === table.table ? 'primary.main' : 'transparent',
                cursor: 'grab',
              }}
            >
              <DragIndicator fontSize="small" sx={{ color: 'text.disabled' }} />
              <IconButton
                aria-label={isHidden
                  ? t(($) => $.userInspector.showTable)
                  : t(($) => $.userInspector.hideTable)}
                onClick={() => onToggleHidden(table.table)}
                size="small"
              >
                {isHidden
                  ? <VisibilityOff fontSize="small" />
                  : <Visibility fontSize="small" />}
              </IconButton>
              {isHidden ? (
                <Box sx={{ opacity: 0.5, whiteSpace: 'nowrap' }}>
                  <Text category="body-small">{label}</Text>
                </Box>
              ) : (
                <Link
                  component="button"
                  onClick={() => onJump(table.table)}
                  sx={{ textAlign: 'left', whiteSpace: 'nowrap' }}
                  underline="hover"
                  variant="body2"
                >
                  {label}
                </Link>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

const UserHeader = ({
  retailerName,
  row,
  userAccountId,
}: {
  retailerName: string,
  row: Record<string, unknown>,
  userAccountId: string,
}) => {
  const { t } = useTranslation();
  const fullName = [row.firstName, row.lastName].map(formatCell).filter(Boolean).join(' ');
  const fields: { label: string, value: string, separatorBefore?: boolean }[] = [
    { label: t(($) => $.userInspector.fields.name), value: fullName || '—' },
    {
      label: t(($) => $.userInspector.fields.business),
      value: formatCell(row.businessName) || '—',
    },
    { label: t(($) => $.userInspector.fields.userAccountId), value: userAccountId },
    {
      label: t(($) => $.userInspector.fields.rewardsAccountId),
      value: formatCell(row.rewardsAccountId) || '—',
    },
    {
      label: t(($) => $.userInspector.fields.walletAccountId),
      value: formatCell(row.walletAccountId) || '—',
    },
    {
      label: t(($) => $.userInspector.fields.retailerName),
      separatorBefore: true,
      value: retailerName || '—',
    },
    {
      label: t(($) => $.userInspector.fields.retailerId),
      value: formatCell(row.retailerId) || '—',
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        position: 'sticky',
        px: 4,
        py: 1.5,
        top: '64px',
        zIndex: 2,
      }}
    >
      <Stack columnGap={4} direction="row" flexWrap="wrap" justifyContent="center" rowGap={1}>
        {fields.map((field) => (
          <Fragment key={field.label}>
            {field.separatorBefore && <Divider flexItem orientation="vertical" />}
            <Box sx={{ textAlign: 'left' }}>
              <Text category="label-small" fontSize={12}>{field.label}</Text>
              <Text category="body-small" fontSize={13}>{field.value}</Text>
            </Box>
          </Fragment>
        ))}
      </Stack>
    </Box>
  );
};

// Sort on the displayed text, with numeric-aware comparison so numbers and
// ISO dates order naturally rather than lexically.
const compareCells = (a: unknown, b: unknown): number =>
  formatCell(a).localeCompare(formatCell(b), undefined, { numeric: true });

const InspectedTable = ({
  onCountChange,
  pageSize,
  table,
}: {
  onCountChange: (table: string, count: number) => void,
  pageSize: number,
  table: UserInspectorEndpoint.GetUserData.InspectedTable,
}) => {
  const { t } = useTranslation();

  const [filter, setFilter] = useState('');
  // Default to the column the server ordered by, so the sort arrow reflects it.
  const [sortColumn, setSortColumn] = useState<string | undefined>(table.orderBy[0]);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [expanded, setExpanded] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showSql, setShowSql] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [extraRows, setExtraRows] = useState<Record<string, unknown>[]>([]);
  const [nextOffset, setNextOffset] = useState(table.rows.length);
  const [hasMore, setHasMore] = useState(table.capped);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    setExtraRows([]);
    setNextOffset(table.rows.length);
    setHasMore(table.capped);
  }, [table]);

  const allRows = useMemo(() => [...table.rows, ...extraRows], [table.rows, extraRows]);
  const columns = useMemo(() => (allRows.length ? Object.keys(allRows[0]) : []), [allRows]);

  useEffect(() => {
    onCountChange(table.table, allRows.length);
  }, [table.table, allRows.length, onCountChange]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const onCopySql = () => {
    void navigator.clipboard.writeText(table.reproductionSql.join('\n'))
      .then(() => setCopied(true))
      .catch(() => undefined);
  };

  // Export the ENTIRE table (all pages, not just what's loaded) to a CSV file.
  const onExportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const rows: Record<string, unknown>[] = [];
      let offset = 0;
      for (;;) {
        const response = await UserInspectorApi.getTableRows({
          filters: table.filters, limit: 1000, offset, table: table.table,
        });
        rows.push(...response.rows);
        offset += response.rows.length;
        if (!response.hasMore || response.rows.length === 0) break;
      }

      const cols = rows.length ? Object.keys(rows[0]) : columns;
      const escape = (value: unknown): string => {
        const text = formatCell(value);
        return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
      };
      const csv = [
        cols.join(','),
        ...rows.map((row) => cols.map((col) => escape(row[col])).join(',')),
      ].join('\n');

      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const anchor = document.createElement('a');
      anchor.download = `${table.table}.csv`;
      anchor.href = url;
      anchor.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const onSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const onLoadMore = async () => {
    setLoadingMore(true);
    try {
      const response = await UserInspectorApi.getTableRows({
        filters: table.filters,
        limit: pageSize,
        offset: nextOffset,
        table: table.table,
      });
      setExtraRows((prev) => [...prev, ...response.rows]);
      setNextOffset((prev) => prev + response.rows.length);
      setHasMore(response.hasMore);
    } finally {
      setLoadingMore(false);
    }
  };

  const visibleRows = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const filtered = needle
      ? allRows.filter((row) =>
        columns.some((col) => formatCell(row[col]).toLowerCase().includes(needle)))
      : allRows;

    if (!sortColumn) return filtered;
    const factor = sortDirection === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => factor * compareCells(a[sortColumn], b[sortColumn]));
  }, [allRows, columns, filter, sortColumn, sortDirection]);

  const countLabel = filter.trim()
    ? `${visibleRows.length} / ${allRows.length}`
    : `${allRows.length}`;

  const headers = columns.map((col) => (
    <TableSortLabel
      active={sortColumn === col}
      direction={sortColumn === col ? sortDirection : 'asc'}
      key={col}
      onClick={() => onSort(col)}
    >
      {col}
    </TableSortLabel>
  ));

  return (
    <Paper
      id={sectionElementId(table.table)}
      sx={{ scrollMarginTop: `${STICKY_TOP}px` }}
      variant="outlined"
    >
      <Stack alignItems="center" direction="row" spacing={1} sx={{ p: 1 }}>
        <IconButton
          aria-label={expanded
            ? t(($) => $.userInspector.collapse)
            : t(($) => $.userInspector.expand)}
          onClick={() => setExpanded((prev) => !prev)}
          size="small"
        >
          <ExpandMore
            sx={{
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: '0.2s',
            }}
          />
        </IconButton>
        <Text category="title-small">
          {`${table.table} · ${countLabel}`}
          {hasMore ? ` ${t(($) => $.userInspector.capped)}` : ''}
        </Text>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={() => setShowSql((prev) => !prev)}
          size="small"
          testID={`user-inspector-togglesql-${table.table}`}
          variant="text"
        >
          {showSql ? t(($) => $.userInspector.hideSql) : t(($) => $.userInspector.showSql)}
        </Button>
        <Tooltip title={t(($) => $.userInspector.downloadCsv)}>
          <IconButton
            aria-label={t(($) => $.userInspector.downloadCsv)}
            data-testid={`user-inspector-export-${table.table}`}
            onClick={() => { void onExportCsv().catch(() => undefined); }}
            size="small"
          >
            {exporting ? <CircularProgress size={20} /> : <GridOn fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Search
          height={36}
          onChangeText={setFilter}
          testID={`user-inspector-filter-${table.table}`}
          value={filter}
          width={220}
        />
      </Stack>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ p: 2, pt: 0 }}>
          {showSql && (
            <>
              <Box sx={{ position: 'relative' }}>
                <IconButton
                  aria-label={t(($) => $.userInspector.copySql)}
                  onClick={onCopySql}
                  size="small"
                  sx={{ position: 'absolute', right: 4, top: 4 }}
                >
                  {copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                </IconButton>
                <Box
                  component="pre"
                  sx={{
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    fontSize: 12,
                    m: 0,
                    overflowX: 'auto',
                    p: 2,
                    pr: 6,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {table.reproductionSql.join('\n')}
                </Box>
              </Box>
              <VSpacer size="4" />
            </>
          )}
          <Box sx={{ overflowX: 'auto' }}>
            <Table
              headers={headers}
              rows={visibleRows.map((row) => columns.map((col) => (
                <Text category="body-small" fontSize={13} key={col}>{formatCell(row[col])}</Text>
              )))}
            />
          </Box>
          {visibleRows.length === 0 && (
            <>
              <VSpacer size="3" />
              <Text category="body-small">{t(($) => $.userInspector.noMatchingRows)}</Text>
            </>
          )}
          {hasMore && (
            <>
              <VSpacer size="3" />
              <Button
                loading={loadingMore}
                onClick={() => { void onLoadMore().catch(() => undefined); }}
                testID={`user-inspector-loadmore-${table.table}`}
              >
                {t(($) => $.userInspector.loadMore, { count: pageSize })}
              </Button>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default UserInspector;
